// CRM: builds the customer view (families, children, lifecycle, timeline, lifetime value, duplicate suggestions)
// from the raw Firestore data. Pure; the teacher portal runs it in the browser. See docs/crm-design.md.

import type {
  CrmChild,
  CrmCustomer,
  CrmHolidayInvoice,
  CrmInput,
  CrmNote,
  CrmPreschoolInvoice,
  Identity,
  MergeSuggestion,
  Money,
  Service,
  Stage,
  TimelineEvent,
} from "./crmTypes";
import { STAGE_LABEL, STAGES } from "./crmTypes";
import {
  clusterIdentities,
  groupChildren,
  holidayInvoiceChildKey,
  identitiesFrom,
  preschoolInvoiceKey,
  suggestMerges,
  type Cluster,
} from "./crmIdentity";
import { childStageOf, familyStageOf, holidayFactsOf, holidayUntil, preschoolStatusOf } from "./crmLifecycle";
import { DEFAULT_ASSUMPTIONS, populationStats, predictChild, weeklyFee, type LtvAssumptions, type PopulationStats } from "./crmLtv";
import { canonicalEmail, isTermId, normaliseName, schoolStart, termName, toIsoDate } from "./crmNormalise";

export * from "./crmTypes";
export * from "./crmNormalise";
export * from "./crmIdentity";
export * from "./crmLifecycle";
export * from "./crmLtv";

interface Ledger {
  preschool: Money;
  holiday: Money;
  preschoolInvoices: CrmPreschoolInvoice[];
}

const emptyLedger = (): Ledger => ({
  preschool: { invoicedPence: 0, paidPence: 0 },
  holiday: { invoicedPence: 0, paidPence: 0 },
  preschoolInvoices: [],
});

const add = (money: Money, invoicedPence: number, paid: boolean) => {
  money.invoicedPence += invoicedPence;
  if (paid) money.paidPence += invoicedPence;
};

const sumMoney = (items: Money[]): Money => ({
  invoicedPence: items.reduce((s, m) => s + m.invoicedPence, 0),
  paidPence: items.reduce((s, m) => s + m.paidPence, 0),
});

/** Splits a family holiday invoice between its children by the value of the days each booked. */
export const allocateHolidayInvoice = (invoice: CrmHolidayInvoice): number[] => {
  const values = invoice.children.map((child) => child.days.reduce((sum, day) =>
    sum + (day.choice === "FULL" ? invoice.fullDayPricePence : day.choice === "HALF" ? invoice.halfDayPricePence : 0), 0));
  const total = values.reduce((s, v) => s + v, 0);
  const shares = values.map((v) => (total > 0 ? Math.floor((invoice.totalPence * v) / total) : Math.floor(invoice.totalPence / values.length)));
  if (shares.length) shares[shares.length - 1]! += invoice.totalPence - shares.reduce((s, v) => s + v, 0);
  return shares;
};

export interface CrmStats {
  customers: number;
  byStage: Record<Stage, number>;
  bothServices: number;
  invoicedPence: number;
  paidPence: number;
  predictedPence: number;
  /** Families with a future (active, registered, holiday only) */
  activeCustomers: number;
}

export interface CrmResult {
  customers: CrmCustomer[];
  suggestions: MergeSuggestion[];
  population: PopulationStats;
  stats: CrmStats;
  identities: Map<string, Identity>;
}

const mostCommon = (values: string[]) => {
  const counts = new Map<string, number>();
  values.forEach((v) => counts.set(v, (counts.get(v) ?? 0) + 1));
  return [...counts].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "";
};

const uniqueBy = (values: string[], key: (v: string) => string) => {
  const seen = new Map<string, string>();
  for (const value of values) if (value && !seen.has(key(value))) seen.set(key(value), value);
  return [...seen.values()];
};

export const buildCrm = (input: CrmInput, today: string, a: LtvAssumptions = DEFAULT_ASSUMPTIONS): CrmResult => {
  const identities = identitiesFrom(input);
  const byKey = new Map(identities.map((i) => [i.key, i]));
  const clusters = clusterIdentities(identities, input.customers, input.rejectedPairs);
  const childIds = new Set(input.children.map((c) => c.id));

  // Money and invoice events, per identity.
  const ledgers = new Map<string, Ledger>();
  const ledger = (key: string) => {
    if (!ledgers.has(key)) ledgers.set(key, emptyLedger());
    return ledgers.get(key)!;
  };
  const invoiceEvents = new Map<string, TimelineEvent[]>();
  const addEvent = (key: string, event: TimelineEvent) => invoiceEvents.set(key, [...(invoiceEvents.get(key) ?? []), event]);

  for (const invoice of input.preschoolInvoices) {
    const key = preschoolInvoiceKey(invoice, childIds);
    const at = toIsoDate(invoice.issuedDate);
    if (!invoice.cancelled) {
      add(ledger(key).preschool, invoice.totalPence, invoice.paid);
      ledger(key).preschoolInvoices.push(invoice);
    }
    if (at) {
      addEvent(key, {
        at, kind: "invoice", service: "preschool", future: at > today,
        title: `Invoice ${invoice.ref}${invoice.cancelled ? " (cancelled)" : ""}`,
        detail: `${invoice.childName} · ${invoice.period || (isTermId(invoice.termId) ? termName(invoice.termId!) : invoice.kind)}`,
        amountPence: invoice.cancelled ? null : invoice.totalPence,
      });
    }
    const paidAt = toIsoDate(invoice.paidDate);
    if (invoice.paid && !invoice.cancelled && paidAt) {
      addEvent(key, { at: paidAt, kind: "payment", service: "preschool", future: false, title: `Paid ${invoice.ref}`, detail: invoice.childName, amountPence: invoice.totalPence });
    }
  }

  for (const invoice of input.holidayInvoices) {
    const shares = allocateHolidayInvoice(invoice);
    const keys = invoice.children.map((child) =>
      byKey.has(`hol:${child.registrationId}`) ? `hol:${child.registrationId}` : holidayInvoiceChildKey(invoice, child.name));
    if (!invoice.cancelled) keys.forEach((key, i) => add(ledger(key).holiday, shares[i]!, invoice.paid));
    const firstDay = invoice.children.flatMap((c) => c.days).map((d) => d.date).filter((d): d is string => !!d).sort()[0] ?? null;
    const at = toIsoDate(invoice.issuedDate) ?? firstDay;
    const owner = keys[0];
    if (!owner) continue;
    if (at) {
      addEvent(owner, {
        at, kind: "invoice", service: "holiday", future: at > today,
        title: `Holiday invoice #${invoice.invoiceNo}${invoice.cancelled ? " (cancelled)" : ""}`,
        detail: invoice.children.map((c) => c.name).join(", "),
        amountPence: invoice.cancelled ? null : invoice.totalPence,
      });
    }
    const paidAt = toIsoDate(invoice.paidDate);
    if (invoice.paid && !invoice.cancelled && paidAt) {
      addEvent(owner, { at: paidAt, kind: "payment", service: "holiday", future: false, title: `Paid #${invoice.invoiceNo}`, detail: "", amountPence: invoice.totalPence });
    }
  }

  // First pass: families, children, stages, realised money.
  interface Draft {
    cluster: Cluster;
    customer: CrmCustomer;
    childContext: Map<string, { startDate: string | null; invoicedTerms: string[]; enquiryPreschool: boolean }>;
  }
  const drafts: Draft[] = [];

  for (const cluster of clusters) {
    const members = cluster.keys.map((k) => byKey.get(k)!).filter(Boolean);
    const docs = [cluster.doc, ...cluster.extraDocs].filter((d): d is NonNullable<typeof d> => !!d);
    const labels = Object.assign({}, ...docs.slice().reverse().map((d) => d.childGroups ?? {})) as Record<string, string>;
    const childInfo = Object.assign({}, ...docs.slice().reverse().map((d) => d.childInfo ?? {})) as Record<string, { dateOfBirth?: string | null }>;
    const childContext = new Map<string, { startDate: string | null; invoicedTerms: string[]; enquiryPreschool: boolean }>();

    const children: CrmChild[] = groupChildren(members, labels).map((group) => {
      const ids = group.identities;
      const named = ids.find((i) => i.kind === "preschoolChild") ?? ids.find((i) => i.kind === "preschoolRegistration") ??
        [...ids].sort((x, y) => (y.at ?? "").localeCompare(x.at ?? ""))[0]!;
      const manualDob = ids.map((i) => childInfo[i.key]?.dateOfBirth).find((d) => !!d) ?? null;
      const sourceDob = ids.find((i) => i.dateOfBirth && i.kind !== "enquiry")?.dateOfBirth ?? null;
      const enquiryDob = ids.find((i) => i.kind === "enquiry" && i.dateOfBirth)?.dateOfBirth ?? null;
      const dateOfBirth = manualDob ?? sourceDob ?? enquiryDob;
      const dobSource = manualDob ? "manual" : sourceDob ? "registration" : enquiryDob ? "enquiry" : null;
      const preschool = preschoolStatusOf(ids, dateOfBirth, today, a);
      const holiday = holidayFactsOf(ids, today);
      const stage = childStageOf(preschool, holiday, dateOfBirth, today, a);
      const ledgersOf = ids.map((i) => ledgers.get(i.key) ?? emptyLedger());
      const invoices = ledgersOf.flatMap((l) => l.preschoolInvoices);
      const services = (["preschool", "holiday"] as Service[]).filter((s) => ids.some((i) => i.service === s));
      childContext.set(group.key, {
        startDate: ids.map((i) => i.preschool?.startDate ?? null).filter((d): d is string => !!d).sort().pop() ?? null,
        invoicedTerms: invoices.filter((i) => i.kind === "term" && isTermId(i.termId)).map((i) => i.termId!),
        enquiryPreschool: ids.some((i) => i.enquiry && i.enquiry.interest !== "holiday"),
      });
      return {
        key: group.key,
        name: named.childName,
        dateOfBirth,
        dobSource,
        identityKeys: ids.map((i) => i.key),
        services,
        preschoolStatus: preschool.status,
        holidayBookings: holiday.bookings,
        firstHoliday: holiday.first,
        lastHoliday: holiday.last,
        schoolStart: dateOfBirth ? schoolStart(dateOfBirth) : null,
        holidayUntil: dateOfBirth ? holidayUntil(dateOfBirth) : null,
        stage: stage.stage,
        stageReason: stage.reason,
        preschool: sumMoney(ledgersOf.map((l) => l.preschool)),
        holiday: sumMoney(ledgersOf.map((l) => l.holiday)),
        weekly: weeklyFee(invoices, ids, a),
        prediction: { preschoolPence: 0, holidayPence: 0, lines: [] },
      };
    });

    const hasEnquiry = members.some((m) => m.kind === "enquiry");
    const derived = familyStageOf(children.map((c) => ({ stage: c.stage, reason: c.stageReason })), hasEnquiry);
    const override = docs.find((d) => d.stageOverride)?.stageOverride ?? null;
    const notes: CrmNote[] = docs.flatMap((d) => d.notes ?? []).sort((x, y) => y.at.localeCompare(x.at));

    const allLedgers = members.map((m) => ledgers.get(m.key) ?? emptyLedger());
    const preschoolMoney = sumMoney(allLedgers.map((l) => l.preschool));
    const holidayMoney = sumMoney(allLedgers.map((l) => l.holiday));

    // Timeline
    const events: TimelineEvent[] = [];
    for (const identity of members) {
      events.push(...(invoiceEvents.get(identity.key) ?? []));
      if (identity.kind === "enquiry" && identity.at) {
        events.push({
          at: identity.at, kind: "enquiry", service: null, future: false, title: "Enquiry",
          detail: [identity.childName, identity.enquiry?.interest === "both" ? "Preschool and Holiday Club"
            : identity.enquiry?.interest === "holiday" ? "Holiday Club" : "Preschool"].filter(Boolean).join(" · "),
          amountPence: null,
        });
      }
      if ((identity.kind === "preschoolChild" || identity.kind === "preschoolRegistration") && identity.at) {
        events.push({ at: identity.at, kind: "registration", service: "preschool", future: false, title: "Preschool registration", detail: identity.childName, amountPence: null });
      }
      const start = identity.preschool?.startDate;
      if (start && identity.kind !== "invoiceOnly") {
        events.push({
          at: start, kind: "start", service: "preschool", future: start > today,
          title: start > today ? "Starts preschool" : "Started preschool", detail: identity.childName, amountPence: null,
        });
      }
      if (identity.kind === "holidayBooking" && identity.at && identity.holiday) {
        const h = identity.holiday;
        const days = [h.fullDays && `${h.fullDays} full`, h.halfDays && `${h.halfDays} half`].filter(Boolean).join(", ") || "no days";
        events.push({
          at: identity.at, kind: h.cancelled ? "cancelledBooking" : "booking", service: "holiday", future: false,
          title: `${h.cancelled ? "Cancelled booking" : "Booked"} · ${h.termName}`, detail: `${identity.childName}: ${days}`, amountPence: null,
        });
      }
    }
    for (const child of children) {
      if (child.schoolStart && (child.preschoolStatus === "active" || child.preschoolStatus === "registered" || child.preschoolStatus === "left")) {
        const future = child.schoolStart > today;
        if (future || child.preschoolStatus === "left") {
          events.push({ at: child.schoolStart, kind: "school", service: null, future, title: future ? "Starts school" : "Started school", detail: child.name, amountPence: null });
        }
      }
    }
    for (const note of notes) events.push({ at: note.at, kind: "note", service: null, future: false, title: "Note", detail: note.text, amountPence: null });
    if (override) {
      events.push({ at: override.at, kind: "stage", service: null, future: false, title: `Stage set to ${STAGE_LABEL[override.stage]}`, detail: override.note, amountPence: null });
    }
    events.sort((x, y) => y.at.localeCompare(x.at));
    const activityDates = events.filter((e) => !e.future && e.kind !== "note" && e.kind !== "stage").map((e) => e.at.slice(0, 10));

    const preferred = members.find((m) => m.kind === "preschoolChild" && !m.preschool?.left) ?? members.find((m) => m.kind === "preschoolChild");
    const parentNames = members.flatMap((m) => m.parentNames);
    const childSurname = mostCommon(children.map((c) => c.name.split(/\s+/).slice(-1)[0] ?? "").filter(Boolean));
    const name = docs.find((d) => d.displayName)?.displayName || preferred?.parentNames[0] || mostCommon(parentNames) ||
      (childSurname ? `${childSurname} family` : "Unknown");

    const customer: CrmCustomer = {
      id: cluster.id,
      doc: cluster.doc,
      extraDocIds: cluster.extraDocs.map((d) => d.id),
      name,
      emails: uniqueBy(members.flatMap((m) => m.emails), (e) => canonicalEmail(e) || e.toLowerCase()),
      phones: uniqueBy(members.flatMap((m) => m.phones), (p) => p.replace(/\D/g, "")),
      addresses: uniqueBy(members.map((m) => m.address), (x) => normaliseName(x)),
      identities: members,
      children,
      services: (["preschool", "holiday"] as Service[]).filter((s) => members.some((m) => m.service === s)),
      stage: override?.stage ?? derived.stage,
      derivedStage: derived.stage,
      stageReason: override ? override.note || "Set by hand" : derived.reason,
      manualStage: !!override,
      firstSeen: activityDates.length ? activityDates.reduce((x, y) => (x < y ? x : y)) : null,
      lastActivity: activityDates.length ? activityDates.reduce((x, y) => (x > y ? x : y)) : null,
      timeline: events,
      notes,
      realised: {
        ...sumMoney([preschoolMoney, holidayMoney]),
        preschoolPence: preschoolMoney.invoicedPence,
        holidayPence: holidayMoney.invoicedPence,
      },
      prediction: { preschoolPence: 0, holidayPence: 0, lines: [] },
      ltvPence: 0,
    };
    drafts.push({ cluster, customer, childContext });
  }

  // Second pass: forecasts, which need the averages over everyone.
  const population = populationStats(drafts.flatMap((d) => d.customer.children), today, a);
  for (const { customer, childContext } of drafts) {
    for (const child of customer.children) {
      const stage = customer.manualStage ? customer.stage : child.stage;
      const context = childContext.get(child.key)!;
      // An enquiry about preschool is forecast as if registered (its retention is the enquiry stage's).
      const forecastChild = child.stage === "enquiry" && context.enquiryPreschool ? { ...child, preschoolStatus: "registered" as const } : child;
      child.prediction = predictChild(forecastChild, { stage, ...context }, population, today, a);
    }
    customer.prediction = {
      preschoolPence: customer.children.reduce((s, c) => s + c.prediction.preschoolPence, 0),
      holidayPence: customer.children.reduce((s, c) => s + c.prediction.holidayPence, 0),
      lines: customer.children.flatMap((c) => c.prediction.lines),
    };
    customer.ltvPence = customer.realised.invoicedPence + customer.prediction.preschoolPence + customer.prediction.holidayPence;
  }

  const customers = drafts.map((d) => d.customer);
  const suggestions = suggestMerges(clusters, byKey, input.rejectedPairs);
  const byStage = Object.fromEntries(STAGES.map((s) => [s, customers.filter((c) => c.stage === s).length])) as Record<Stage, number>;
  return {
    customers,
    suggestions,
    population,
    identities: byKey,
    stats: {
      customers: customers.length,
      byStage,
      bothServices: customers.filter((c) => c.services.length === 2).length,
      invoicedPence: customers.reduce((s, c) => s + c.realised.invoicedPence, 0),
      paidPence: customers.reduce((s, c) => s + c.realised.paidPence, 0),
      predictedPence: customers.reduce((s, c) => s + c.prediction.preschoolPence + c.prediction.holidayPence, 0),
      activeCustomers: byStage.active + byStage.registered + byStage.holidayOnly,
    },
  };
};
